import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Globe, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/mojrakde', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Submission failed');

      toast.success('Message sent successfully!', {
        description: 'Thank you for reaching out. I\'ll get back to you soon.',
      });

      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or contact me directly via email.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'mailvaibhav.vishnu@gmail.com', href: 'mailto:mailvaibhav.vishnu@gmail.com', color: '#8B5CF6' },
    { icon: Phone, label: 'Phone', value: '+91-8983290525', href: 'tel:+918983290525', color: '#06B6D4' },
    { icon: MapPin, label: 'Location', value: 'Pune → Open to Relocate', href: null, color: '#A78BFA' },
    { icon: Linkedin, label: 'LinkedIn', value: 'linkedin.com/in/vaibhav-vishnu', href: 'https://linkedin.com/in/vaibhav-vishnu-a48205107', color: '#8B5CF6' },
    { icon: Globe, label: 'Website', value: 'www.vaibhavvishnu.com', href: 'https://www.vaibhavvishnu.com', color: '#06B6D4' }
  ];

  return (
    <section id="contact" className="py-14 relative">
      <Toaster richColors position="top-right" />
      
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2A1F5E]/30 to-[#2A1F5E]/50" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-white/10 backdrop-blur-md border border-[#8B5CF6]/30 rounded-full text-[#8B5CF6] text-xs font-mono font-semibold tracking-wider">
              GET IN TOUCH
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white" data-testid="contact-heading">
            Start a Conversation
          </h2>
          <p className="text-white/60 text-base font-mono">Let's discuss how I can drive results for Datadog</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6"
          >
            <div className="flex items-center space-x-3 mb-5">
              <Sparkles className="text-[#06B6D4]" size={24} style={{ filter: 'drop-shadow(0 0 8px #06B6D4)' }} />
              <h3 className="text-2xl font-bold text-white">Contact Information</h3>
            </div>
            
            <div className="space-y-3">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 bg-white/5 backdrop-blur-sm border border-white/5 rounded-xl p-4 transition-all"
                  data-testid={`contact-info-${index}`}
                >
                  <div 
                    className="p-3 rounded-lg bg-white/5 flex-shrink-0"
                    style={{ boxShadow: `0 0 15px ${info.color}40` }}
                  >
                    <info.icon size={20} style={{ color: info.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-white/50 mb-1 font-mono">{info.label}</div>
                    {info.href ? (
                      <a 
                        href={info.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-[#06B6D4] transition-colors text-sm"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <div className="text-white text-sm">{info.value}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6"
          >
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/70 mb-2 font-mono">
                  &gt; Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#8B5CF6] focus:outline-none transition-colors backdrop-blur-sm"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-2 font-mono">
                  &gt; Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#06B6D4] focus:outline-none transition-colors backdrop-blur-sm"
                  placeholder="your.email@company.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-white/70 mb-2 font-mono">
                  &gt; Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#8B5CF6] focus:outline-none transition-colors backdrop-blur-sm"
                  placeholder="Your company"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white/70 mb-2 font-mono">
                  &gt; Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#06B6D4] focus:outline-none transition-colors resize-none backdrop-blur-sm"
                  placeholder="Your message..."
                  data-testid="contact-message-input"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white rounded-lg px-6 py-4 font-semibold transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ boxShadow: '0 0 30px rgba(139, 92, 246, 0.4)' }}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <span>Send Message</span>
                    <Send size={18} />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;